import { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import {
  Box,
  Button,
  TextField,
  MenuItem,
  Grid,
  Autocomplete,
  CircularProgress,
  Card,
  Alert,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
} from '@mui/material';
import { Save, Cancel } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { donationsAPI, contactsAPI, trustMastersAPI } from '../services/api';
import { colors } from '../theme/modernTheme';
import { showSuccess, showError } from '../utils/toast';
import { getDateWithOffset, getDateWithYearOffset, getTodayDate } from '../utils/dateUtils';
import {
  validateDonationForm,
  parseBackendErrors,
  hasErrors as checkHasErrors,
} from '../utils/formValidation';
import {
  saveDonationFormState,
  clearDonationFormState,
  selectSavedDonationFormState,
} from '../store/slices/donationFormSlice';
import {
  selectDonationSchemas,
  selectDonationSchemasLoading,
} from '../store/slices/donationSchemasSlice';
import { selectGlobalTags, selectGlobalTagsStatus } from '../store/slices/tagsSlice';
import TagAutocomplete from './TagAutocomplete';

const DonationPledgeForm = forwardRef(function DonationPledgeForm(
  { pledgeId, pledgeData, onSuccess, onCancel },
  ref,
) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const savedFormState = useSelector(selectSavedDonationFormState);
  const newContactId = searchParams.get('newContactId');

  // Donation schemas from Redux (loaded in App.js on authentication)
  const donationSchemas = useSelector(selectDonationSchemas);
  const loadingSchemas = useSelector(selectDonationSchemasLoading);

  // Tags from Redux (loaded in App.js on authentication)
  const tags = useSelector(selectGlobalTags);
  const tagsStatus = useSelector(selectGlobalTagsStatus);

  const [formData, setFormData] = useState({
    contact_id: '',
    amount: '',
    pledge_start_date: getDateWithOffset(1),
    pledge_end_date: getDateWithYearOffset(),
    next_pledge_date: '', // Add next_pledge_date field
    frequency: 'MONTHLY',
    pledge_type: 'recurring', // 'one_time' or 'recurring'
    donation_schema: '',
    trust_master_id: '',
    purpose: '',
    collected_amount: 0,
    pledge_status: 'PENDING', // PENDING, PAUSED (CLOSED is computed)
  });

  const [contacts, setContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [selectedSchema, setSelectedSchema] = useState(null);
  const [selectedTags, setSelectedTags] = useState([]);
  const [trustMasters, setTrustMasters] = useState([]);
  const [selectedTrustMaster, setSelectedTrustMaster] = useState(null);
  const [loadingTrustMasters, setLoadingTrustMasters] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [errors, setErrors] = useState({});
  const [contactsPage, setContactsPage] = useState(1);
  const [hasMoreContacts, setHasMoreContacts] = useState(true);
  const [contactSearchTerm, setContactSearchTerm] = useState('');
  const [debouncedContactSearch, setDebouncedContactSearch] = useState('');
  const [initialContactLoaded, setInitialContactLoaded] = useState(false);

  // Debounce contact search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedContactSearch(contactSearchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [contactSearchTerm]);

  // Load contacts when debounced search changes
  useEffect(() => {
    // Skip initial load if in edit mode and contact hasn't been loaded yet
    if (pledgeId && !initialContactLoaded) {
      return;
    }

    // Reset to page 1 when search term changes
    setContactsPage(1);
    setHasMoreContacts(true);
    // Clear contacts list before loading new search results
    setContacts([]);
    loadContacts(1, debouncedContactSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedContactSearch, initialContactLoaded]);

  // Restore form state when returning from contact creation
  useEffect(() => {
    if (savedFormState && !pledgeId) {
      setFormData(savedFormState.formData);
      setSelectedSchema(savedFormState.selectedSchema);
      setInitialContactLoaded(true);
      // Don't clear yet - wait for contact selection
    }
  }, [savedFormState, pledgeId]);

  // Handle newly created contact
  useEffect(() => {
    if (newContactId && savedFormState) {
      // Load the newly created contact
      loadNewlyCreatedContact(newContactId);
    }
  }, [newContactId, savedFormState]);

  // Load existing pledge data if editing (only after schemas are loaded)
  useEffect(() => {
    if (pledgeId && donationSchemas.length > 0 && !pledgeData) {
      // Only fetch if pledgeData is not provided
      loadPledge();
    } else if (pledgeData && donationSchemas.length > 0) {
      // Use provided pledgeData
      populatePledgeData(pledgeData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pledgeId, donationSchemas.length]); // pledgeData intentionally excluded to prevent re-running

  // Load trust masters on component mount
  useEffect(() => {
    loadTrustMasters();
  }, []);

  // Set selected trust master after trust masters are loaded and formData has trust_master_id
  useEffect(() => {
    if (formData.trust_master_id && trustMasters.length > 0 && !selectedTrustMaster) {
      const trustMaster = trustMasters.find((tm) => tm.id === formData.trust_master_id);
      if (trustMaster) {
        setSelectedTrustMaster(trustMaster);
      }
    }
  }, [formData.trust_master_id, trustMasters, selectedTrustMaster]);

  const loadTrustMasters = async () => {
    try {
      setLoadingTrustMasters(true);
      const response = await trustMastersAPI.getAll({ limit: 1000 });
      const trustMastersList = response?.data?.trustMasters || [];
      setTrustMasters(trustMastersList);
    } catch (error) {
      console.error('Failed to load trust masters:', error);
      showError('Failed to load trust masters');
      setTrustMasters([]);
    } finally {
      setLoadingTrustMasters(false);
    }
  };

  const populatePledgeData = async (pledge) => {
    try {
      setLoading(true);

      // Determine pledge_type from frequency
      const pledgeType =
        pledge.frequency === 'one_time' || pledge.frequency === 'ONE_TIME'
          ? 'one_time'
          : 'recurring';

      // Map pledge_status to form (CLOSED cannot be edited, shown as PENDING)
      // const pledgeStatus =
      //   pledge.pledge_status === "CLOSED"
      //     ? "PENDING"
      //     : pledge.pledge_status || "PENDING";

      setFormData({
        contact_id: pledge.contact_id,
        amount: pledge.amount,
        pledge_start_date: pledge.pledge_start_date,
        pledge_end_date: pledge.pledge_end_date || getTodayDate(),
        next_pledge_date: pledge.next_pledge_date || '',
        frequency: pledge.frequency.toUpperCase(),
        pledge_type: pledgeType,
        donation_schema: pledge.donation_schema,
        trust_master_id: pledge.trust_master_id || '',
        purpose: pledge.purpose || '',
        pledge_status: pledge.pledge_status,
        collected_amount: pledge.collected_amount || 0,
      });

      // Set selected tags if available
      if (pledge.tags && Array.isArray(pledge.tags) && pledge.tags.length > 0) {
        // Use tags array directly from API response
        setSelectedTags(pledge.tags);
      } else if (pledge.tag_ids && Array.isArray(pledge.tag_ids)) {
        // Fallback: match tag_ids with loaded tags
        const pledgeTags = tags.filter((t) => pledge.tag_ids.includes(t.id));
        if (pledgeTags.length > 0) {
          setSelectedTags(pledgeTags);
        }
      } else if (pledge.tag_id) {
        // Backward compatibility: handle single tag_id
        const tag = tags.find((t) => t.id === pledge.tag_id);
        if (tag) {
          setSelectedTags([tag]);
        } else if (pledge.tag_name) {
          // If tag not in array but we have tag_name from backend, create tag object
          setSelectedTags([
            {
              id: pledge.tag_id,
              name: pledge.tag_name,
              color: pledge.tag_color || null,
            },
          ]);
        }
      }

      // Load the specific contact for this pledge
      if (pledge.contact_id) {
        // Create contact object from pledge data (already includes contact info from JOIN)
        const contactFromPledge = {
          id: pledge.contact_id,
          first_name: pledge.first_name,
          last_name: pledge.last_name,
          organization_name: pledge.organization_name,
          type: pledge.contact_type,
          primary_email: pledge.primary_email,
        };
        setSelectedContact(contactFromPledge);
        setInitialContactLoaded(true);
      } else {
        setInitialContactLoaded(true);
      }

      // Set selected schema
      const schema = donationSchemas.find((s) => s.id === pledge.donation_schema);
      if (schema) {
        setSelectedSchema(schema);
      }

      // Set selected trust master
      if (pledge.trust_master_id && trustMasters.length > 0) {
        const trustMaster = trustMasters.find((tm) => tm.id === pledge.trust_master_id);
        if (trustMaster) {
          setSelectedTrustMaster(trustMaster);
        }
      }
    } catch (error) {
      showError('Failed to populate pledge data');
    } finally {
      setLoading(false);
    }
  };

  const loadPledge = async () => {
    try {
      setLoading(true);
      const response = await donationsAPI.getById(pledgeId);
      const pledge = response.data;
      await populatePledgeData(pledge);
    } catch (error) {
      showError('Failed to load pledge data');
      setLoading(false);
    }
  };

  const loadContacts = async (pageNum = 1, searchTerm = '') => {
    try {
      setLoadingContacts(true);
      const params = {
        limit: 20,
        page: pageNum,
        sortBy: 'name',
        sortOrder: 'desc',
      };

      // Add search parameter if provided
      if (searchTerm && searchTerm.trim()) {
        params.search = searchTerm.trim();
      }

      const response = await contactsAPI.getAll(params);

      setContacts((prev) => {
        if (pageNum === 1) {
          // For first page or new search, replace all contacts
          return response.data;
        }
        // For pagination, append new contacts and filter duplicates
        const newContacts = response.data.filter(
          (newC) => !prev.some((existingC) => existingC.id === newC.id),
        );
        return [...prev, ...newContacts];
      });

      const total = response.pagination?.total || 0;
      const loadedCount = (pageNum - 1) * 20 + response.data.length;
      setHasMoreContacts(loadedCount < total);
      setContactsPage(pageNum);
    } catch (error) {
      console.error('Failed to load contacts:', error);
      showError('Failed to load contacts');
    } finally {
      setLoadingContacts(false);
    }
  };

  const loadNewlyCreatedContact = async (contactId) => {
    try {
      setLoadingContacts(true);
      const contactResponse = await contactsAPI.getOne(contactId);
      const contact = contactResponse.data;

      // Set the selected contact
      setSelectedContact(contact);
      setFormData((prev) => ({
        ...prev,
        contact_id: contactId,
      }));

      // Add to contacts list if not already there
      setContacts((prev) => {
        const exists = prev.some((c) => c.id === contactId);
        if (!exists) {
          return [contact, ...prev];
        }
        return prev;
      });

      // Clear saved form state
      dispatch(clearDonationFormState());

      // Remove newContactId from URL
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    } catch (error) {
      console.error('Failed to load newly created contact:', error);
      showError('Failed to load the newly created contact');
      // Clear saved state anyway
      dispatch(clearDonationFormState());
    } finally {
      setLoadingContacts(false);
    }
  };

  const handleAddContact = () => {
    // Save current form state to Redux
    dispatch(
      saveDonationFormState({
        formData,
        selectedSchema,
      }),
    );

    // Navigate to contact creation page with "from=donation" flag
    navigate('/contacts/new?from=donation');
  };

  // Expose handleAddContact to parent via ref
  useImperativeHandle(ref, () => ({
    handleAddContact,
  }));

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Validate amount field - only allow positive integers
    if (name === 'amount') {
      // Allow empty value (user is clearing the field)
      if (value === '') {
        setFormData((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: '' }));
        return;
      }

      // Check for invalid characters (anything other than digits)
      if (!/^\d+$/.test(value)) {
        setErrors((prev) => ({
          ...prev,
          amount: 'Amount must contain only numbers (no decimals, hyphens, or special characters)',
        }));
        return; // Don't update the value
      }

      // Check if it's a valid positive integer
      const numValue = parseInt(value, 10);
      if (isNaN(numValue) || numValue <= 0) {
        setErrors((prev) => ({
          ...prev,
          amount: 'Amount must be a positive number',
        }));
        return; // Don't update the value
      }

      // Check maximum amount (50 crores = 500,000,000)
      const MAX_AMOUNT = 500000000;
      if (numValue > MAX_AMOUNT) {
        setErrors((prev) => ({
          ...prev,
          amount: `Amount cannot exceed ₹50 crores (₹${MAX_AMOUNT.toLocaleString('en-IN')})`,
        }));
        return; // Don't update the value
      }

      // Valid amount - clear error and update
      setFormData((prev) => ({ ...prev, [name]: value }));
      setErrors((prev) => ({ ...prev, [name]: '' }));
      return;
    }

    // Normalize frequency to uppercase for storage
    const normalizedValue = name === 'frequency' ? value.toUpperCase() : value;

    setFormData((prev) => ({
      ...prev,
      [name]: normalizedValue,
    }));

    // Clear error for this field
    setErrors((prev) => ({
      ...prev,
      [name]: '',
    }));

    // Handle pledge_type change
    if (name === 'pledge_type') {
      if (value === 'one_time') {
        // Set frequency to ONE_TIME and sync end date with start date
        setFormData((prev) => ({
          ...prev,
          pledge_type: 'one_time',
          frequency: 'ONE_TIME',
          pledge_end_date: prev.pledge_start_date,
        }));
      } else {
        // Set frequency to MONTHLY (default for recurring)
        setFormData((prev) => ({
          ...prev,
          pledge_type: 'recurring',
          frequency: 'MONTHLY',
        }));
      }
    }

    // Handle frequency change (for recurring pledges)
    if (name === 'frequency' && normalizedValue === 'ONE_TIME') {
      setFormData((prev) => ({
        ...prev,
        pledge_type: 'one_time',
        pledge_end_date: prev.pledge_start_date,
      }));
    }

    // Sync pledge_end_date with pledge_start_date for ONE_TIME
    if (name === 'pledge_start_date' && formData.pledge_type === 'one_time') {
      setFormData((prev) => ({
        ...prev,
        pledge_end_date: value,
      }));
    }
  };

  const handleSchemaChange = (_event, newValue) => {
    setSelectedSchema(newValue);
    setFormData((prev) => ({
      ...prev,
      donation_schema: newValue?.id || '',
    }));
    setErrors((prev) => ({
      ...prev,
      donation_schema: '',
    }));
  };

  const validate = () => {
    // Use the validation utility
    const newErrors = validateDonationForm(
      formData,
      formData?.pledge_type,
      formData?.collected_amount,
    );
    setErrors(newErrors);
    return !checkHasErrors(newErrors);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      // Errors are already set by validate() function
      return;
    }

    try {
      setLoading(true);

      // Prepare submit data with proper field mapping for backend API
      const submitData = {
        contact_id: formData.contact_id,
        amount: parseInt(formData.amount, 10),
        pledge_start_date: formData.pledge_start_date,
        pledge_end_date: formData.pledge_end_date,
        frequency: formData.frequency.toUpperCase(), // Ensure uppercase (ONE_TIME, MONTHLY, etc.)
        donation_schema: formData.donation_schema, // This is already the schema ID
        trust_master_id: formData.trust_master_id, // Add trust_master_id
        purpose: formData.purpose || null,
        tag_ids: selectedTags.map((tag) => tag.id), // Send array of tag IDs
        is_paused: formData.pledge_status === 'PAUSED', // Map status to is_paused
      };

      // Include next_pledge_date only when editing and it has a value
      if (pledgeId && formData.next_pledge_date) {
        submitData.next_pledge_date = formData.next_pledge_date;
      }

      if (pledgeId) {
        await donationsAPI.update(pledgeId, submitData);
        showSuccess('Pledge updated successfully');
      } else {
        await donationsAPI.create(submitData);
        showSuccess('Pledge created successfully');
      }

      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Form submission error:', error);

      // Parse backend errors into field-level errors
      const backendErrors = parseBackendErrors(error);

      // Set field-level errors
      if (checkHasErrors(backendErrors)) {
        setErrors(backendErrors);
      }

      // Show general error message only if there's a _general error
      if (backendErrors._general) {
        showError(backendErrors._general);
      } else if (!checkHasErrors(backendErrors)) {
        // If no specific errors were parsed, show a generic error
        showError('Failed to save pledge');
      }
    } finally {
      setLoading(false);
    }
  };

  const getContactLabel = (contact) => {
    if (!contact) return '';
    if (contact.type === 'INDIVIDUAL') {
      return `${contact.first_name} ${contact.last_name || ''}`.trim();
    }
    return contact.organization_name || '';
  };

  return (
    <Card sx={{ p: 3, borderRadius: '4px' }}>
      <form onSubmit={handleSubmit}>
        {/* General Error Alert */}
        {errors._general && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {errors._general}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Donor Selection */}
          <Grid item xs={12}>
            <Autocomplete
              options={contacts}
              getOptionLabel={getContactLabel}
              value={selectedContact}
              onChange={(_e, newValue) => {
                const newContactId = newValue?.id || '';
                setSelectedContact(newValue);
                setFormData((prev) => ({
                  ...prev,
                  contact_id: newContactId,
                }));

                // Clear search term when a contact is selected
                if (newValue) {
                  setContactSearchTerm('');
                }

                // Clear error
                setErrors((prev) => ({
                  ...prev,
                  contact_id: '',
                }));
              }}
              onInputChange={(_event, newInputValue, reason) => {
                // Update search term when user types
                if (reason === 'input') {
                  setContactSearchTerm(newInputValue);
                }
                // When user clears the input, reset to show all contacts
                if (reason === 'clear' || (reason === 'input' && !newInputValue)) {
                  setContactSearchTerm('');
                  setSelectedContact(null);
                }
              }}
              filterOptions={(x) => x} // Disable client-side filtering - using server-side search
              noOptionsText={
                loadingContacts
                  ? 'Searching...'
                  : contactSearchTerm
                    ? 'No contacts found'
                    : 'Type to search contacts'
              }
              loading={loadingContacts}
              ListboxProps={{
                onScroll: (event) => {
                  const listboxNode = event.currentTarget;
                  if (
                    !loadingContacts &&
                    hasMoreContacts &&
                    listboxNode.scrollTop + listboxNode.clientHeight >=
                      listboxNode.scrollHeight - 50
                  ) {
                    loadContacts(contactsPage + 1, debouncedContactSearch);
                  }
                },
                style: { maxHeight: '300px' },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Donor *"
                  placeholder="Type to search contacts..."
                  error={!!errors.contact_id}
                  helperText={errors.contact_id}
                  FormHelperTextProps={{
                    sx: { marginLeft: 0 },
                  }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingContacts && <CircularProgress color="inherit" size={20} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>

          {/* Amount */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Pledged Amount *"
              name="amount"
              type="text"
              inputMode="numeric"
              value={formData.amount}
              onChange={handleChange}
              error={!!errors.amount}
              helperText={errors.amount}
              placeholder="Enter amount (numbers only)"
              FormHelperTextProps={{
                sx: { marginLeft: 0 },
              }}
            />
          </Grid>

          {/* Donation Schema */}
          <Grid item xs={12} md={6}>
            <Autocomplete
              value={selectedSchema}
              onChange={handleSchemaChange}
              options={donationSchemas}
              getOptionLabel={(option) => option.name || ''}
              loading={loadingSchemas}
              filterOptions={(options, state) => {
                // Optimize filtering for large lists
                if (!state.inputValue) return options;
                const inputValue = state.inputValue.toLowerCase();
                return options.filter((option) => {
                  const name = (option.name || '').toLowerCase();
                  return name.includes(inputValue);
                });
              }}
              renderInput={(params) => (
                <TextField
                  FormHelperTextProps={{
                    sx: { marginLeft: 0 },
                  }}
                  {...params}
                  label="Donation Scheme *"
                  error={!!errors.donation_schema}
                  helperText={
                    errors.donation_schema ||
                    `${pledgeId ? 'Updating Donation Scheme will also update the associated Transactions.' : ''}`
                  }
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingSchemas && <CircularProgress color="inherit" size={20} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              ListboxProps={{
                style: { maxHeight: '250px' },
              }}
            />
          </Grid>

          {/* Trust Master */}
          <Grid item xs={12} md={12}>
            <Autocomplete
              value={selectedTrustMaster}
              onChange={(event, newValue) => {
                setSelectedTrustMaster(newValue);
                setFormData((prev) => ({
                  ...prev,
                  trust_master_id: newValue?.id || '',
                }));
                setErrors((prev) => ({
                  ...prev,
                  trust_master_id: '',
                }));
              }}
              options={Array.isArray(trustMasters) ? trustMasters : []}
              getOptionLabel={(option) => option?.trustName || ''}
              loading={loadingTrustMasters}
              disabled={!!pledgeId} // Disable when editing
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              renderInput={(params) => (
                <TextField
                  FormHelperTextProps={{
                    sx: { marginLeft: 0 },
                  }}
                  {...params}
                  label="Trust *"
                  error={!!errors.trust_master_id || !!errors.id}
                  helperText={
                    errors.trust_master_id ||
                    (pledgeId ? (
                      <span style={{ color: '#ed6c02', fontWeight: 500 }}>
                        Trust cannot be changed after pledge creation. All transactions are linked
                        to this trust.
                      </span>
                    ) : (
                      <span style={{ color: '#ed6c02', fontWeight: 500 }}>
                        ⚠️ Warning: Trust cannot be changed after pledge creation. Choose carefully.
                      </span>
                    ))
                  }
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingTrustMasters && <CircularProgress color="inherit" size={20} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              ListboxProps={{
                style: { maxHeight: '250px' },
              }}
            />
          </Grid>

          {/* Pledge Type Radio Buttons */}
          <Grid item xs={12} md={12}>
            <Box display="flex" alignItems="center" gap={2}>
              <FormLabel component="legend" sx={{ fontWeight: 500, minWidth: 'auto' }}>
                Pledge Type *
              </FormLabel>
              <RadioGroup
                row
                name="pledge_type"
                value={formData.pledge_type}
                onChange={handleChange}
              >
                <FormControlLabel value="one_time" control={<Radio />} label="One-time" />
                <FormControlLabel value="recurring" control={<Radio />} label="Recurring" />
              </RadioGroup>
            </Box>
          </Grid>

          {/* Frequency (only for recurring) */}
          {formData.pledge_type === 'recurring' && (
            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                label="Frequency *"
                name="frequency"
                value={formData.frequency.toLowerCase()}
                onChange={handleChange}
                error={!!errors.frequency}
                helperText={errors.frequency}
                FormHelperTextProps={{
                  sx: { marginLeft: 0 },
                }}
              >
                <MenuItem value="monthly">Monthly</MenuItem>
                <MenuItem value="quarterly">Quarterly</MenuItem>
                <MenuItem value="half_yearly">Half-yearly</MenuItem>
                <MenuItem value="yearly">Yearly</MenuItem>
              </TextField>
            </Grid>
          )}

          {/* Pledge Start Date */}
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Pledge Start Date *"
              name="pledge_start_date"
              type="date"
              value={formData.pledge_start_date}
              onChange={handleChange}
              error={!!errors.pledge_start_date}
              helperText={errors.pledge_start_date}
              FormHelperTextProps={{
                sx: { marginLeft: 0 },
              }}
              onClick={(e) => e.target.showPicker?.()}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {/* Pledge End Date */}
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Pledge End Date *"
              name="pledge_end_date"
              type="date"
              value={formData.pledge_end_date}
              onChange={handleChange}
              error={!!errors.pledge_end_date}
              onClick={(e) => e.target.showPicker?.()}
              helperText={
                errors.pledge_end_date
                // (formData.pledge_type === "one_time"
                //   ? "Auto-set to start date for one-time pledges"
                //   : "")
              }
              FormHelperTextProps={{
                sx: { marginLeft: 0 },
              }}
              InputLabelProps={{ shrink: true }}
              disabled={formData.pledge_type === 'one_time'}
            />
          </Grid>

          {/* Next Pledge Date - Only show for recurring pledges in edit mode */}
          {pledgeId && formData.pledge_type === 'recurring' && (
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Next Pledge Date"
                name="next_pledge_date"
                type="date"
                value={formData.next_pledge_date || ''}
                onChange={handleChange}
                error={!!errors.next_pledge_date}
                helperText={errors.next_pledge_date || 'When the next reminder will be generated'}
                FormHelperTextProps={{
                  sx: { marginLeft: 0 },
                }}
                onClick={(e) => e.target.showPicker?.()}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          )}

          {/* Pledge Status - Only show in edit mode */}
          {pledgeId && (
            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                label="Pledge Status"
                name="pledge_status"
                value={formData.pledge_status}
                onChange={handleChange}
                error={!!errors.pledge_status}
                helperText={errors.pledge_status}
                FormHelperTextProps={{
                  sx: { marginLeft: 0 },
                }}
              >
                <MenuItem value="PENDING">Pending</MenuItem>
                <MenuItem value="PAUSED">Paused</MenuItem>
                <MenuItem value="CLOSED">Closed</MenuItem>
              </TextField>
            </Grid>
          )}

          {/* Tag */}
          <Grid item xs={12} md={4}>
            <Box>
              <TagAutocomplete
                options={tags}
                value={selectedTags}
                onChange={(event, newValue) => {
                  // newValue is an array of tag objects when multiple={true}
                  setSelectedTags(newValue || []);
                }}
                multiple={true}
                placeholder="Select tags (optional)"
                loading={tagsStatus === 'loading'}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    minHeight: '56px',
                  },
                }}
              />
            </Box>
          </Grid>

          {/* Purpose */}
          <Grid item xs={8}>
            <TextField
              fullWidth
              label="Purpose"
              name="purpose"
              value={formData.purpose}
              onChange={handleChange}
              multiline
              rows={1}
            />
          </Grid>

          {/* Info Alert for ONE_TIME */}
          {formData.pledge_type === 'one_time' && (
            <Grid item xs={12}>
              <Alert severity="info">
                One-time pledges have their end date automatically set to the start date.
              </Alert>
            </Grid>
          )}

          {/* Action Buttons */}
          <Grid item xs={12}>
            <Box display="flex" gap={2} justifyContent="flex-end">
              <Button
                variant="outlined"
                sx={{ borderRadius: '4px' }}
                startIcon={<Cancel />}
                onClick={onCancel}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={<Save />}
                disabled={loading}
                sx={{
                  borderRadius: '4px',
                  backgroundColor: colors.primary.main,
                  '&:hover': { backgroundColor: colors.primary.dark },
                }}
              >
                {loading ? 'Saving...' : pledgeId ? 'Update Pledge' : 'Create Pledge'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Card>
  );
});

export default DonationPledgeForm;
